import dayjs, { Dayjs } from 'dayjs';

import * as api from '@/api';
import * as planner from '@/organisms/holiday-planner';
import { ABSENCE_STATUSES } from '@/views/modules/absences/config/constants';
import { getRecordStyling } from '@/views/modules/absences/utils/calendarColorsUtils';
import {
  AbsenceInterval,
  AbsenceRow,
  AbsenceSelection,
  ChartConfig,
  EmployeeScheduleTaskVariables,
} from './types';

const definedColours = ['toovoimetus', 'kodukontor', 'puhkus', 'lahetus', 'koolitused', 'kontor'];

type AbsenceExtended = Omit<api.AbsenceDto, 'id'> & {
  id?: any;
  businessTrip?: boolean;
  saldo?: number;
}

type SaldosMap = {
  [employee: string]: {
    [vacationType: string]: number;
  };
}

const formClient = new api.FormClient();
const taskClient = new api.TaskClient();

export function parseRows(
  scheduleRows: api.ScheduleRow[],
  config: ChartConfig,
  isSaintGobain: boolean,
  planning: boolean,
  executionId: string,
  employeeSelectionAllowed: (employeeId: string, departmentId: string) => boolean,
) {
  const rows: AbsenceRow[] = [];
  const saldos: SaldosMap = {};
  const absenceMap: { [date: string]: AbsenceInterval } = {};
  const selectionsToParse: api.AbsenceDto[] = [];
  if (scheduleRows) {
    for (const scheduleRow of scheduleRows) {
      const row = getRowFromAbsence(scheduleRow, config);
      if (scheduleRow.absences) {
        for (const absence of scheduleRow.absences) {
          if (
            planning
            && (absence.status === ABSENCE_STATUSES.IN_PLANNING
              || absence.status === ABSENCE_STATUSES.MANAGER_IN_PLANNING)
            && absence.executionId === executionId
            && employeeSelectionAllowed(absence.employee, absence.departmentId)
          ) {
            // TODO:: also check: selectionEnabled, employeeSelectionAllowed
            selectionsToParse.push(absence);
          } else {
            const day = getDaysFromAbsence(absence, config);
            if (day) {
              row.days.push(day);
              absenceMap[day.id] = day;
            }
          }
        }
      }
      if (!isSaintGobain && scheduleRow.businessTrips) {
        for (const businessTrip of scheduleRow.businessTrips) {
          const day = getDaysFromAbsence({
            ...businessTrip,
            absenceType: 'businessTrip',
            businessTrip: true,
          }, config);
          if (day) {
            row.days.push(day);
            absenceMap[day.id] = day;
          }
        }
      }
      saldos[scheduleRow.employee] = scheduleRow.balances.reduce((acc, curr: any) => {
        acc[curr.vacationType] = curr.saldo ? Math.round(curr.saldo) : 0;
        return acc;
      }, {} as { [vacationType: string]: number; });
      rows.push(row);
    }
  }

  const loadedSelections: AbsenceSelection[] = [];
  for (const selectionAbsence of selectionsToParse) {
    const selectionRow = rows.find((row) => row.id === selectionAbsence.employee);

    if (selectionRow) {
      // Push directly if daysBetween is already available
      loadedSelections.push({
        id: selectionAbsence.id,
        recordId: selectionAbsence.id,
        subType: selectionAbsence.subType || selectionAbsence.vacationType,
        vacationPayment: selectionAbsence.vacationPayment,
        studySubType: selectionAbsence.studySubType,
        startDate: dayjs(selectionAbsence.startDate).toDate(),
        endDate: dayjs(selectionAbsence.endDate).toDate(),
        days: getSelectionDaysFromAbsence(selectionAbsence) as any as planner.Day[],
        daysBetween: selectionAbsence.daysBetween,
        daysBetweenn: selectionAbsence.daysBetween,
        row: selectionRow,
      });
    }
  }

  // rows.forEach((employeeRow) => {
  //   if (this.employeeSelectionAllowed(employeeRow.id, employeeRow.departmentId)) {
  //     this.employees.push(employeeRow.id);
  //   }
  // });

  return {
    rows,
    saldos,
    absenceMap,
    loadedSelections,
  };
}

export default parseRows;

function getSelectionDaysFromAbsence(absence: api.AbsenceDto) {
  const days: { date: Dayjs; key: string; }[] = [];
  let indexDay = dayjs(absence.startDate);
  while (indexDay.isSameOrBefore(dayjs(absence.endDate))) {
    days.push({
      date: indexDay,
      key: indexDay.format('yyyy-mm-dd'),
    });
    indexDay = indexDay.add(1, 'day');
  }
  return days;
}

function getRowFromAbsence(absence: AbsenceExtended, config: ChartConfig): AbsenceRow {
  const days = getDaysFromAbsence(absence, config);
  return {
    id: absence.employee,
    title: absence.employeeName,
    subtitle: absence.departmentId,
    maxDays: absence.saldo ? Math.round(absence.saldo) : 0,
    days: days ? [days] : [],
    userId: absence.employee,
    userName: absence.employeeName,
    departmentId: absence.departmentId,
  };
}

function getDaysFromAbsence(absence: AbsenceExtended, config: ChartConfig): AbsenceInterval {
  if (!absence.startDate || !absence.endDate) {
    return null;
  }
  // custom hex colors
  const [colorKey, colour] = getColorFromAbsence(absence, config);
  const interval: AbsenceInterval = {
    id: absence.businessTrip ? `bt-${absence.id}` : absence.id,
    originalId: absence.id,
    employee: absence.employee,
    startDate: dayjs(absence.startDate).toDate(),
    endDate: dayjs(absence.endDate).toDate(),
    absenceType: absence.absenceType || absence.vacationType, // used only for excel week view, to filter by type=vacation
    subType: absence.subType || absence.vacationType,
    colorKey,
    colour,
    businessTrip: absence.businessTrip,
    vacationPayment: absence.vacationPayment,
    studyType: absence.studySubType,
    status: absence.status,
    class: null,
    style: null,
  };
  if (config?.mappings) {
    const styling = getRecordStyling(absence, config.mappings);
    interval.class = styling.class;
    interval.style = styling.style;
  } else if (config?.colorMap) {
    interval.class = (!colour.startsWith('#') && definedColours.includes(colour)) ? colour : 'toovoimetus';
    interval.style = colour.startsWith('#') ? {
      background: colour,
    } : {};
  }
  return interval;
}

function getColorFromAbsence(absence: api.AbsenceDto, config: ChartConfig) {
  if (config && config.colorMap && config.colorField) {
    const colorKey = absence[config.colorField];
    if (colorKey && config.colorMap[colorKey]) {
      return [colorKey, config.colorMap[colorKey]];
    }
  }
  return ['toovoimetus'];
}

export async function getValidationIds(formId: string) {
  const form = await formClient.getForm({ formId });
  let singleValidationIds: { submit?: string[]; save?: string[] } = {};
  let validationIds: { submit?: string[]; save?: string[] } = {};

  function find(obj: any) {
    if (obj.allOf) {
      for (const o of obj.allOf) {
        find(o);
      }
    }
    if (obj.oneOf) {
      for (const o of obj.oneOf) {
        find(o);
      }
    }
    if (obj.properties) {
      for (const prop in obj.properties) {
        find(obj.properties[prop]);
      }
    }

    if (obj.config?.singleValidationIds) {
      singleValidationIds = obj.config.singleValidationIds;
    }
    if (obj.config?.validationIds) {
      validationIds = obj.config.validationIds;
    }
  }

  find(form.model);

  return {
    singleValidationIds,
    validationIds,
  };
}

export async function loadTasksWithVariables(
  filterByAssignee: any,
  filterByTaskName?: string,
  formKeys?: string[],
) {
  const tasks: EmployeeScheduleTaskVariables[] = [];
  try {
    const response = await taskClient.getAllTasks({
      filterByAssignee,
      ...(filterByTaskName && { filterByTaskName }),
    });

    // Filter by formKeys before fetching variables to reduce API calls
    const filteredTasks = formKeys && formKeys.length > 0
      ? response.data.filter((task) => formKeys.includes(task.formKey))
      : response.data;

    const tasksWithVariables = await Promise.all(filteredTasks.map(async (item) => {
      const variables = await taskClient.getTaskVariables({ id: item.id });
      return {
        ...variables,
        task: item,
      } as EmployeeScheduleTaskVariables;
    }));
    tasks.push(...tasksWithVariables);
  } catch (err) {
    console.error('Error loading schedules', err);
  }
  return tasks;
}
